import React, { useEffect } from "react"
import { CreditRequestDetailLineModel } from "@/pages/CreditRequest/CreditRequestDetail";
import { Grid2 as Grid, MenuItem, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getTimestampInLocalTime } from "@/util/dateUtil";
import { pascalEnumToString } from "@/util/jbhTextFormatterUtil";
import QuantitySelector from "../Common/QuantitySelector/QuantitySelector";
import { LargeActionButton } from "../Common/LargeActionButton";
import PageContext from "../Common/PageContext";
import { ReversalItem } from "@/common/api/Models/types";
import { getReversibleQuantity } from "@/util/creditReversalUtil";

const reversalReasonType = "Reversal";

export interface CreditReversalModalContentProps {
    lineItems: CreditRequestDetailLineModel[];
    reversalItems: ReversalItem[];
    updateReversalItem: (sku: string, changes: Partial<ReversalItem>) => void;
    copyReasonToAll: (reason: string) => void;
} 

export const CreditReversalModalContent: React.FC<CreditReversalModalContentProps> = ({ 
    lineItems,
    reversalItems,
    updateReversalItem,
    copyReasonToAll,
}) => {
    const classes = useStyles();
    const [pageContext] = React.useContext(PageContext);
    const reasons = (pageContext.creditRequestReasons ?? [])
        .filter((reason) => reason.type === reversalReasonType)
        .map((reason) => reason.value);

    const getReversalItem = (sku: string) => reversalItems.find((item) => item.sku === sku);
    const showCopyReasonToAll = lineItems.length > 1;

    return (
        <Grid container size={12}>
            {lineItems.map((li, index) => (
                <React.Fragment key={li.id}>
                    <Grid container size={12} className={classes.lineItem}>
                        <Grid size={12} className={classes.lineItemHeader}>
                            <Typography variant="body2">
                                {li.description}
                            </Typography>
                        </Grid>
                        <Grid size={8}>
                            <Typography variant="body2">
                                SKU: <span className={classes.value}>{li.sku}</span>
                            </Typography>
                            <Typography variant="body2">
                                Model: <span className={classes.value}>{li.modelNumber}</span>
                            </Typography>
                            <Typography variant="body2">
                                Date Created: <span className={classes.value}>{getTimestampInLocalTime(li.dateCreated, "dd/MM/yyyy")}</span>
                            </Typography>
                        </Grid>
                        <Grid size={4}>
                            <Typography variant="body2">
                                Credited: {li.quantity}
                            </Typography>
                            <Typography variant="caption" display="block" className={classes.label}>
                                Unit Ex: <span className={classes.value}>${li.unitCostEx.toFixed(2)}</span>
                            </Typography>
                            <Typography variant="caption" display="block">
                                Line Inc: ${li.lineCostInc.toFixed(2)}
                            </Typography>
                        </Grid>
                        <Grid size={12} className={classes.reversedField}>
                            <Typography variant="body2">
                                Credits Previously Reversed: {li.reversedQuantity}
                            </Typography>
                        </Grid>
                        <Grid container size={12} spacing={1} alignItems="center" wrap="nowrap">
                            <Grid size="auto">
                                <QuantitySelector
                                    productQuantity={getReversalItem(li.sku)?.quantity ?? getReversibleQuantity(li)}
                                    max={getReversibleQuantity(li)}
                                    onQuantityChange={(quantity) => updateReversalItem(li.sku, { quantity })}
                                />
                            </Grid>
                            <Grid size="grow">
                                <TextField
                                    id={`reversal-reason-${li.id}`}
                                    select
                                    label="Reason"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={getReversalItem(li.sku)?.reason ?? ""}
                                    onChange={(event) => updateReversalItem(li.sku, { reason: event.target.value })}
                                    className={classes.field}
                                    slotProps={{
                                        inputLabel: { shrink: Boolean(getReversalItem(li.sku)?.reason) },
                                    }}
                                >
                                    {reasons.map((reason) => (
                                        <MenuItem key={reason} value={reason}>
                                            {pascalEnumToString(reason)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                        {showCopyReasonToAll && index === 0 && (
                            <Grid size={12} className={classes.copyReasonRow}>
                                <LargeActionButton
                                    variant="outlined"
                                    onClick={() => copyReasonToAll(getReversalItem(li.sku)?.reason ?? "")}
                                    disabled={!getReversalItem(li.sku)?.reason}
                                    className={classes.copyButton}
                                >
                                    Copy Reason To All
                                </LargeActionButton>
                            </Grid>
                        )}
                    </Grid>
                </React.Fragment>
            ))}
        </Grid>
    )
}

const useStyles = makeStyles((theme) => ({
	lineItem: {
		padding: theme.spacing(2),
		backgroundColor: theme.palette.action.hover,
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
    lineItemHeader: {
        paddingBottom: theme.spacing(1),
    },
    label: {
        fontWeight: 700,
    },
    value: {
        fontWeight: 400,
    },
    reversedField: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    field: {
        backgroundColor: theme.palette.custom.creditRequest.fieldBackground,
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
        "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            transform: "translate(14px, 10px) scale(1)",
        },
    },
    copyReasonRow: {
        paddingTop: theme.spacing(1.5),
    },
    copyButton: {
        height: "2.5rem",
        backgroundColor: theme.palette.custom.creditRequest.fieldBackground,
        color: theme.palette.text.primary,
    },
}))